// ============================================================
// standing - a character saved AS THEY STAND, and brought back.
//
// The library keeps sheets. A character's condition (wounds, conditions,
// critical damage, ammunition, worn armour, cover, pins) rides beside the
// sheet as one optional block, copied whole (see StandingBlock). These
// are the only functions that put it on, take it off, or read it.
//
// Pure. Nothing here mutates the entry or the combatant it was given.
// ============================================================
import type { Character } from '../character/types';
import { sameValue } from '../canonicalJson';
import { combatantFromCharacter, emptyCombatState, withoutStanding } from './factories';
import type { IdentityChoice } from './identity';
import type { Combatant, CombatState } from './types';

/** The library entry for a combatant as they stand: their sheet, with the live state and pins beside it. */
export function withStanding(c: Combatant, now: number = Date.now()): Character {
  // The write clock is the roster's, not the library's: a restored state starts its own.
  const state = { ...c.state, rev: 0 };
  return { ...sheetOf(c.sheet), standing: { state, overrides: { ...c.overrides }, savedAt: now }, updatedAt: now };
}

/**
 * A library entry for EACH of these combatants as they stand. Several
 * bodies built from one sheet (five Ork Boyz) share its id, and would
 * overwrite each other: the first keeps the sheet's entry, each later one
 * gets an entry of its own under its table name. That id is derived from
 * the combatant's, so saving again lands on the same entries.
 */
export function standingEntries(combatants: readonly Combatant[], now: number = Date.now()): Character[] {
  const seen = new Set<string>();
  return combatants.map((c) => {
    const entry = withStanding(c, now);
    if (!seen.has(entry.id)) {
      seen.add(entry.id);
      return entry;
    }
    return { ...entry, id: `${entry.id}-${c.id}`, name: c.name };
  });
}

/** The entry's sheet alone. The same object when it carries no condition. */
export function sheetOf(entry: Character): Character {
  return withoutStanding(entry);
}

/** The keys of `saved` that `current` still has, each with its saved value. */
function keptKeys<T>(saved: Record<string, T> | undefined, current: Record<string, T>): Record<string, T> {
  const kept = Object.keys(current).filter((key) => saved?.[key] !== undefined);
  return Object.fromEntries(kept.map((key) => [key, (saved as Record<string, T>)[key]]));
}

type Ammo = CombatState['weaponAmmo'];

/**
 * The rounds left in each weapon the sheet still has, in the magazine it
 * has NOW: capacity and reload are the sheet's (it may have been edited
 * since), and a magazine cannot hold more than its capacity.
 */
function fittedAmmo(saved: Ammo | undefined, fresh: Ammo): Ammo {
  const fitted = Object.entries(fresh).map(([id, now]) => {
    const left = saved?.[id]?.current;
    return [id, left === undefined ? now : { ...now, current: Math.min(left, now.max) }];
  });
  return Object.fromEntries(fitted);
}

/**
 * The saved state made to fit the sheet as it is NOW. The sheet may have
 * been edited since: a weapon swapped, a body changed. So ammunition and
 * armour are kept only for the weapons and locations the sheet still has
 * (new ones start fresh), and any field an older block lacks takes its
 * fresh value.
 */
export function reconcileStanding(entry: Character): CombatState {
  const sheet = sheetOf(entry);
  const fresh = emptyCombatState(sheet);
  const saved = entry.standing?.state;
  if (!saved) return fresh;
  return {
    ...fresh,
    ...saved,
    rev: 0,
    armour: { ...fresh.armour, ...keptKeys(saved.armour, fresh.armour) },
    weaponAmmo: fittedAmmo(saved.weaponAmmo, fresh.weaponAmmo),
  };
}

type CombatantOptions = Parameters<typeof combatantFromCharacter>[1];

/** A combatant built from a library entry AS THEY STAND; fresh when the entry carries no condition. */
export function combatantFromStanding(entry: Character, opts: CombatantOptions = {}): Combatant {
  if (!entry.standing) return combatantFromCharacter(entry, opts);
  return combatantFromCharacter(sheetOf(entry), {
    ...opts,
    state: reconcileStanding(entry),
    overrides: { ...entry.standing.overrides },
  });
}

/**
 * Does adding this entry "as they stand" restore its saved condition?
 * Not over "update the sheet, keep wounds": that choice is a promise
 * that the live state stays, and it outranks the saved one.
 */
export function restoresStanding(choice: IdentityChoice | undefined, asStands: boolean): boolean {
  return asStands && choice !== 'update-sheet';
}

type Halves = Pick<Combatant, 'sheet' | 'overrides' | 'state'>;

/**
 * Armour worn ABOVE the sheet's, made the sheet's: for a library sheet being
 * written, where a number typed in a location's big field means "this is its
 * armour". Worn below the sheet stays damage (the saved condition). Without
 * this, a GM's armour landed only in the condition - "3 of 0" - and anything
 * that builds the figure fresh from its sheet (a scene's cast) wore none
 * (the user, 2026-10-20). The same halves when nothing is raised.
 */
export function issueWornArmour(halves: Halves): Halves {
  const issued = halves.sheet.armour?.locations ?? {};
  const raised = Object.entries(halves.state.armour ?? {}).filter(([id, worn]) => worn > (issued[id] ?? 0));
  if (!raised.length) return halves;
  const locations = { ...issued, ...Object.fromEntries(raised) };
  return { ...halves, sheet: { ...halves.sheet, armour: { ...halves.sheet.armour, locations } } };
}

/** A state's content apart from its write clock, for comparing. */
const content = ({ rev: _rev, ...rest }: CombatState) => rest;

/**
 * A library entry from a sheet and the condition it is in: the block is
 * kept only while there is something to keep. A state that is exactly
 * the sheet's fresh state, with nothing pinned, is no condition at all,
 * and the entry goes back to being a clean template.
 */
export function entryWithCondition(
  halves: Pick<Combatant, 'sheet' | 'overrides' | 'state'>,
  savedAt: number = Date.now(),
): Character {
  const sheet = sheetOf(halves.sheet);
  const pinned = Object.keys(halves.overrides ?? {}).length > 0;
  const fresh = sameValue(content(halves.state), content(emptyCombatState(sheet)));
  if (fresh && !pinned) return sheet;
  return { ...sheet, standing: { state: { ...halves.state, rev: 0 }, overrides: { ...halves.overrides }, savedAt } };
}

/**
 * A combatant already in the roster, put into the condition the entry
 * saved: its state and pins. The state clock moves on, so every client
 * takes the restored state as newer than the fresh one it replaces.
 */
export function asTheyStood(c: Combatant, entry: Character): Combatant {
  if (!entry.standing) return c;
  const state = {
    ...reconcileStanding({ ...entry, ...c.sheet, standing: entry.standing }),
    rev: (c.state.rev ?? 0) + 1,
  };
  return { ...c, state, overrides: { ...entry.standing.overrides }, updatedAt: Date.now() };
}

/** "9 / 22 wounds · 2 conditions · 1 critical" - what a saved condition holds, for whoever is about to restore it. */
export function standingSummary(entry: Character): string | null {
  if (!entry.standing) return null;
  const state = reconcileStanding(entry);
  const sheet = sheetOf(entry);
  const health = sheet.horde?.isHorde
    ? `magnitude ${state.hordeMagnitude} / ${sheet.horde.maxMagnitude}`
    : `${state.woundsCurrent} / ${sheet.wounds?.max ?? state.woundsCurrent} wounds`;
  const counted = (n: number, one: string, many: string) => (n ? [`${n} ${n === 1 ? one : many}`] : []);
  return [
    health,
    ...counted(state.conditions.length, 'condition', 'conditions'),
    ...counted(state.criticalDamage.length, 'critical', 'criticals'),
    ...counted(Object.keys(entry.standing.overrides).length, 'pin', 'pins'),
  ].join(' · ');
}
